// Image handling for menu photos: downscale on the client (keeps uploads small and
// the demo snappy), then either upload to Firebase Storage or return an inline
// data URL when running without a backend.
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "./firebase";
import { isBackendConfigured } from "./catalog";

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

/** Downscale an image file to at most maxDim on the long edge; JPEG. */
export async function resizeImage(
  file: File,
  maxDim = 800,
  quality = 0.72,
): Promise<{ blob: Blob; dataUrl: string }> {
  const img = await loadImage(file);
  const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  ctx.drawImage(img, 0, 0, w, h);
  const dataUrl = canvas.toDataURL("image/jpeg", quality);
  const blob = await new Promise<Blob>((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), "image/jpeg", quality),
  );
  return { blob, dataUrl };
}

/**
 * Store a daily-item photo and return its URL.
 * Demo mode → inline data URL (shows instantly, not persisted).
 * Backend   → uploaded to Firebase Storage under menu/daily/.
 */
export async function uploadDailyImage(id: string, file: File): Promise<string> {
  const { blob, dataUrl } = await resizeImage(file);
  if (!isBackendConfigured) return dataUrl;
  const objectRef = ref(storage, `menu/daily/${id}-${Date.now()}.jpg`);
  await uploadBytes(objectRef, blob, { contentType: "image/jpeg" });
  return getDownloadURL(objectRef);
}
